// The WebGPU GPU timer the client reports `gpuMs` from (R431, R505).
//
// This is the WebGPU twin of `GpuTimerRing` in `gputimer.ts`. It is a second
// file, because `perf/` imports nothing from `fast/` and a backend must not
// carry the types of the other backend. The seam test holds `GPUDevice` to
// this file inside `perf/`.
//
// WebGL2 measures one span with `beginQuery` and `endQuery`. WebGPU writes a
// timestamp at the start and at the end of a RENDER PASS, so a frame that
// opens more than one pass gives more than one span. The ring hands out one
// pair of query slots for each pass, and `poll` adds the spans of one frame
// together. The sum holds the clear and the offscreen field render, which is
// the figure R431 asks for.
//
// The discipline is the one the WebGL2 ring keeps: a map buffer that waits
// for the driver is never touched, the ring hands out no more slots than the
// query set holds, and a figure that cannot be true is thrown away.
//
// The timer does not run unless the page asks for the harness.

/** How many map buffers the ring holds. */
const RING_BUFFERS = 4;

/** How many bytes one resolved timestamp query takes. */
const QUERY_BYTES = 8;

/**
 * The largest frame the ring accepts, in nanoseconds. A GPU counter that
 * resets between the two writes gives a delta of minutes. The ring throws
 * such a frame away, as the WebGL2 ring throws a disjoint result away.
 */
const MAX_FRAME_NS = 1e9;

// `GPUBufferUsage` and `GPUMapMode` are values the DOM library declares no
// type for, and a test environment supplies neither. The ring names the four
// buffer flags and the one map flag it uses.
const USAGE_MAP_READ = 0x0001;
const USAGE_COPY_SRC = 0x0004;
const USAGE_COPY_DST = 0x0008;
const USAGE_QUERY_RESOLVE = 0x0200;
const MAP_MODE_READ = 0x0001;

/** One frame of timestamps that waits for its figures. */
interface PendingFrame {
  readonly buffer: GPUBuffer;
  readonly passes: number;
}

/**
 * The WebGPU timestamp ring.
 *
 * The device must carry the `timestamp-query` feature. A device without it
 * makes an inert ring: `passTimestamps` gives `undefined`, `resolve` and
 * `poll` do nothing, and `lastMs` stays `null`.
 */
export class GpuTimestampRing {
  private set: GPUQuerySet | null = null;
  private resolveBuffer: GPUBuffer | null = null;
  private free: GPUBuffer[] = [];
  private pending: PendingFrame[] = [];
  /** The frame the driver maps now. Only one frame maps at a time. */
  private inFlight: PendingFrame | null = null;
  /** True when the driver mapped `inFlight` and `poll` can read it. */
  private ready = false;
  /** How many passes of the current frame took a pair of slots. */
  private used = 0;
  /** The last completed measurement, in ms. */
  lastMs: number | null = null;

  constructor(
    device: GPUDevice,
    private readonly maxPasses = 8,
  ) {
    if (!device.features || !device.features.has('timestamp-query')) return;
    const bytes = 2 * maxPasses * QUERY_BYTES;
    this.set = device.createQuerySet({ type: 'timestamp', count: 2 * maxPasses });
    this.resolveBuffer = device.createBuffer({
      size: bytes,
      usage: USAGE_QUERY_RESOLVE | USAGE_COPY_SRC,
    });
    for (let i = 0; i < RING_BUFFERS; i++) {
      this.free.push(device.createBuffer({ size: bytes, usage: USAGE_MAP_READ | USAGE_COPY_DST }));
    }
  }

  /**
   * The timestamp writes for ONE render pass of the current frame.
   *
   * The caller puts the result in the render pass descriptor. It is
   * `undefined` when the feature is absent, or when the frame already used
   * every pair the query set holds. A pass without writes is not measured,
   * and the frame is still valid.
   */
  passTimestamps(): GPURenderPassTimestampWrites | undefined {
    const set = this.set;
    if (!set || this.used >= this.maxPasses) return undefined;
    const pass = this.used++;
    return {
      querySet: set,
      beginningOfPassWriteIndex: 2 * pass,
      endOfPassWriteIndex: 2 * pass + 1,
    };
  }

  /**
   * Copies the timestamps of the frame into a map buffer.
   *
   * The caller runs this after the last pass of the frame and outside every
   * pass, because `resolveQuerySet` is a command and not a draw. A frame that
   * used no pass resolves nothing. A frame that finds every map buffer busy
   * goes unmeasured, which keeps the ring free of a wait.
   */
  resolve(encoder: GPUCommandEncoder): void {
    const set = this.set;
    const staging = this.resolveBuffer;
    const passes = this.used;
    this.used = 0;
    if (!set || !staging || passes === 0) return;
    const buffer = this.free.pop();
    if (!buffer) return;
    const bytes = 2 * passes * QUERY_BYTES;
    encoder.resolveQuerySet(set, 0, 2 * passes, staging, 0);
    encoder.copyBufferToBuffer(staging, 0, buffer, 0, bytes);
    this.pending.push({ buffer, passes });
  }

  /**
   * Reads the oldest frame the driver mapped, then starts the next map.
   *
   * The caller runs this after `submit`. The ring maps one buffer at a time,
   * because a map that is in flight must not get a second request and the
   * figures of one frame arrive together.
   */
  poll(): void {
    const done = this.inFlight;
    if (done && this.ready) {
      this.inFlight = null;
      this.ready = false;
      this.read(done);
      done.buffer.unmap();
      this.free.push(done.buffer);
    }
    if (this.inFlight) return;
    const next = this.pending.shift();
    if (!next) return;
    this.inFlight = next;
    next.buffer.mapAsync(MAP_MODE_READ, 0, 2 * next.passes * QUERY_BYTES).then(
      () => {
        if (this.inFlight === next) this.ready = true;
      },
      () => {
        // The map failed, because the ring was destroyed or the device went
        // away. The buffer does not go back to the free list: its state is
        // not known.
        if (this.inFlight === next) this.inFlight = null;
      },
    );
  }

  /** Destroys the query set and every buffer. The ring is inert after this. */
  destroy(): void {
    if (this.set) this.set.destroy();
    if (this.resolveBuffer) this.resolveBuffer.destroy();
    for (const buffer of this.free) buffer.destroy();
    for (const frame of this.pending) frame.buffer.destroy();
    if (this.inFlight) this.inFlight.buffer.destroy();
    this.set = null;
    this.resolveBuffer = null;
    this.free = [];
    this.pending = [];
    this.inFlight = null;
    this.ready = false;
    this.used = 0;
  }

  /**
   * Adds the spans of one mapped frame into `lastMs`.
   *
   * The timestamps are nanoseconds in a 64-bit counter. An end before its
   * begin, or a sum above `MAX_FRAME_NS`, means the counter reset: the frame
   * goes away and `lastMs` keeps the figure it holds.
   */
  private read(frame: PendingFrame): void {
    const words = new BigUint64Array(
      frame.buffer.getMappedRange(0, 2 * frame.passes * QUERY_BYTES),
    );
    let ns = 0;
    for (let i = 0; i < frame.passes; i++) {
      const begin = words[2 * i];
      const end = words[2 * i + 1];
      if (end < begin) return;
      ns += Number(end - begin);
    }
    if (ns > MAX_FRAME_NS) return;
    this.lastMs = ns / 1e6;
  }
}
